import { ErrorBoundary } from '@awais4123/fillify-design-library';
import { DesktopPageHeader } from 'components/DesktopPageHeader';
import { Flex } from 'components/Flex';
import { MobilePageHeader } from 'components/MobilePageHeader/MobilePageHeader';
import { PageWrapper } from 'components/PageWrapper';
import { SettingsNavigation } from 'components/SettingsNavigation';
import { SetupBanner } from 'components/SetupBanner/SetupBanner';
import { useDimensionsRef } from 'hooks';
import { LocationSettingsPageRedirect } from 'pages/SettingsPage/LocationSettingsPageRedirect';
import { SettingsLandingPage } from 'pages/SettingsLandingPage';
import { LocationSettingsPage } from 'pages/LocationSettingsPage';
import { SettingsPageViewTemplate } from 'pages/SettingsPage/SettingsPage.styled';
import { FC, Fragment } from 'react';
import { Route, RouteProps, Switch, useRouteMatch } from 'react-router-dom';
import { MountTransition } from 'routes';
import { PositionsSettingsPage } from 'pages/SettingsPage/PositionsSettingsPage';
import { PositionsSettingsRedirectPage } from 'pages/SettingsPage/PositionsSettingsPage/PositionsSettingsPageRedirectPage';
import { OrganizationSettingsRedirectPage } from 'pages/SettingsPage/OrganizationSettingsPage/OrganizationSettingsRedirectPage';
import { SessionService } from 'services';
import { WhiteLabelAccessor } from 'utils/whiteLabelAccessor';
import { AdministratorSettingsPage } from 'pages/SettingsPage/AdministratorSettingsPage/AdministratorSettingsPage';
import { AdministratorSettingsRedirectPage } from 'pages/SettingsPage/AdministratorSettingsPage';
import { ShiftDistributionRulesTieringModal } from 'pages/SettingsPage/modals/ShiftDistributionRulesTieringModal';
import { GroupsModal } from 'pages/SettingsPage/modals/GroupsModal';
import { useTranslation } from 'hooks/useTranslation';
import { UserProfilePage } from 'pages/SettingsPage/UserProfilePage';
import { AccushieldDashboardButton } from 'components/AccushieldDashboardButton';
import { CompanyProfilePage } from 'pages/SettingsPage/CompanyProfilePage';
import { ADPProfilePage } from 'pages/ADPProfilePage';
import { ADPPage } from 'pages/ADPAuthRedirectPage/ADPPage';
import { PositionsAllocationModal } from 'pages/SettingsPage/PositionsSettingsPage/B2BPositionsSettingsPage/modals/PositionsByLocation';
import { RemovePositionModal } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal';
import { OrganizationSettingsPage } from 'pages/SettingsPage/OrganizationSettingsPage';

const SettingsModals: FC = () => {
  const isB2bUser = SessionService.assertUserType('B2B');
  return (
    <Fragment>
      <GroupsModal />
      <RemovePositionModal />
      <ShiftDistributionRulesTieringModal />
      {isB2bUser && <PositionsAllocationModal />}
    </Fragment>
  );
};

const SettingsPage: FC<RouteProps> = () => {
  const [ref, { width: x }] = useDimensionsRef<HTMLDivElement>();
  const { t } = useTranslation();
  const { isAccushieldProvider } = WhiteLabelAccessor;
  return (
    <PageWrapper>
      <SetupBanner />
      <DesktopPageHeader title={t('settings:SETTINGS')}>
        <Flex flexDirection="row-reverse" width="100%" gap="5px">
          {isAccushieldProvider && <AccushieldDashboardButton />}
        </Flex>
      </DesktopPageHeader>
      <MobilePageHeader title={t('settings:SETTINGS')} />
      <Flex width="100%" height="100%">
        <SettingsNavigation ref={ref} />
        <SettingsModals />
        <PageViewTemplate x={x} />
      </Flex>
    </PageWrapper>
  );
};

const PageViewTemplate: FC<{ x: number }> = ({ x }) => {
  const { path } = useRouteMatch();
  const isPendingApproval = SessionService.getUser()?.access_status === 'not_approved';

  return (
    <SettingsPageViewTemplate x={x} flexDirection="column" isPendingApproval={isPendingApproval}>
      <ErrorBoundary
        componentName="SettingsModule"
        suppressMessage
        showLogo={!WhiteLabelAccessor.isWhiteLabelled()}
      >
        <MountTransition>
          <Switch>
            <Route exact path={`${path}`} component={SettingsLandingPage} />
            <Route exact path={`${path}/locations`} component={LocationSettingsPageRedirect} />
            <Route path={`${path}/locations/:locationId`} component={LocationSettingsPage} />
            <Route exact path={`${path}/positions`} component={PositionsSettingsRedirectPage} />
            <Route path={`${path}/positions/:id`} component={PositionsSettingsPage} />
            <Route
              exact
              path={`${path}/administrators`}
              component={AdministratorSettingsRedirectPage}
            />
            <Route exact path="/settings/user" component={UserProfilePage} />
            <Route
              exact
              path="/settings/organization"
              component={OrganizationSettingsRedirectPage}
            />
            <Route path="/settings/organization" component={OrganizationSettingsPage} />
            <Route exact path="/settings/company" component={CompanyProfilePage} />
            <Route path={`${path}/administrators/:id`} component={AdministratorSettingsPage} />
            <Route exact path="/settings/adp" component={ADPPage} />
            <Route exact path="/settings/adp/profile" component={ADPProfilePage} />
          </Switch>
        </MountTransition>
      </ErrorBoundary>
    </SettingsPageViewTemplate>
  );
};

export default SettingsPage;
